import { useEffect, useState } from "react";

import styles from "./styles/gamesOverview.module.css";
import { translate } from "../../translations/index.js";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import { Link, useNavigate } from "react-router";
import APIclient from "../../api-client/index.js";
import {
  GameTemplate,
  ObjectId,
  SessionStatus,
  Stored,
  VotingSession,
} from "../../types.js";

export default function GamesOverview() {
  return (
    <div className="container" style={{ marginBottom: "150px" }}>
      <div>
        <div className="content-container">
          <h1>{translate("teacher_overview_games_title")}</h1>
          <p
            dangerouslySetInnerHTML={{
              __html: translate("teacher_overview_games_description")!,
            }}
          ></p>
          <div className={styles.createBtnWrapper}>
            <Link className="button button--sm" to="/teacher/games/new">
              {translate("teacher.gamesOverview.createGame")}
            </Link>
          </div>
        </div>
        <TemplatesTable />
        <SessionsTable />
      </div>
    </div>
  );
}

function TemplatesTable() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Array<Stored<GameTemplate>>>([]);

  const fetchTemplates = async () => {
    const gameTemplates = await APIclient.gameTemplates.index();
    const sortedGameTemplates = gameTemplates.sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
    setTemplates(sortedGameTemplates);
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const createVotingSession = async (gameTemplateId: ObjectId) => {
    const votingSession = await APIclient.sessions.create(gameTemplateId);
    navigate(`/teacher/votingsession/${votingSession.code}`);
  };

  const destroyTemplate = async (id: ObjectId) => {
    if (confirm(translate("teacher_overview_games_delete_game_confirmation"))) {
      await APIclient.gameTemplates.destroy(id);
      fetchTemplates();
    }
  };

  return (
    <>
      <h3>{translate("teacher_overview_games_templates")}</h3>
      <div className={styles.templateHeaderContainer}>
        <div className={`${styles.templateAttr} ${styles.bold}`}>
          {translate("teacher_overview_games_name")}
        </div>
        <div className={`${styles.templateAttr} ${styles.bold}`}>
          {translate("teacher_overview_games_type")}
        </div>
        <div></div>
      </div>
      <div className={styles.templatesWrapper}>
        {templates.map((template) => (
          <div className={styles.templateContainer} key={template._id}>
            <div className={styles.templateAttr}>{template.name}</div>
            <div className={styles.btnWrapper}>
              <button
                className="button"
                onClick={() => createVotingSession(template._id)}
              >
                {translate("teacher_overview_games_start_game")}
              </button>
            </div>
            <Link
              to={`/teacher/games/${template._id}/edit`}
              className={styles["edit-template"]}
            >
              <EditIcon className={styles["edit-template__icon"]} />
            </Link>
            <button
              onClick={() => destroyTemplate(template._id)}
              className={styles["destroy-template"]}
            >
              <DeleteOutlineIcon className={styles["destroy-template__icon"]} />
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

function SessionsTable() {
  const [sessions, setSessions] = useState<Stored<VotingSession>[]>([]);

  useEffect(() => {
    APIclient.sessions.index().then((sessions) => {
      const sortedSessions = sessions.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      setSessions(sortedSessions);
    });
  }, []);

  const sessionLink = (session: VotingSession) => {
    if (session.state === SessionStatus.Results) {
      return `/teacher/votingsession/${session.code}/results`;
    } else {
      return `/teacher/votingsession/${session.code}`;
    }
  };

  return (
    sessions.length > 0 && (
      <div>
        <h3>{translate("teacher_overview_games_active_sessions")}</h3>
        <div className={styles.sessionHeader}>
          <div className={`${styles.sessionAttr} ${styles.bold}`}>
            {translate("teacher_overview_games_status")}
          </div>
          <div className={`${styles.sessionAttr} ${styles.bold}`}>
            {translate("teacher_overview_games_name")}
          </div>
          <div className={`${styles.sessionAttr} ${styles.bold}`}>
            {translate("teacher_overview_games_tempo")}
          </div>
          <div className={`${styles.sessionAttr} ${styles.bold}`}>
            {translate("teacher_overview_games_nr_of_statements")}
          </div>
        </div>
        {sessions.map((session) => (
          <div className={styles.sessionContainer} key={session._id}>
            <div
              className={styles.sessionAttr}
              style={{ textTransform: "capitalize" }}
            >
              {session.state}
            </div>
            <div className={styles.sessionAttr}>
              {session.name || session.code}
            </div>
            <div className={`${styles.sessionAttr} ${styles.lightColor}`}>
              {session.tempo}
            </div>
            <div className={`${styles.sessionAttr} ${styles.lightColor}`}>
              {session.statements.length}
            </div>
            <div className={styles.btnWrapper}>
              <Link to={sessionLink(session)} className="button button--sm">
                {translate("teacher_overview_games_resume")}
              </Link>
            </div>
          </div>
        ))}
      </div>
    )
  );
}
